import { Button, Grid, Snackbar, Tooltip } from "@material-ui/core";
import LockIcon from "@material-ui/icons/Lock";
import SaveIcon from "@material-ui/icons/Save";
import { Alert } from "@material-ui/lab";
import { ChartData } from "chart.js";
import { isNaN, isNil, round } from "lodash-es";
import React, { type Dispatch, useEffect, useState } from "react";
import { ErrorModal } from "../../../../components/modals/error-modal";
import "./ContextMenu.css";
import ColorPicker from "./components/ColorPicker";
import { LoadViewModal } from "./components/modals/loadView/LoadViewModal";
import { SaveAsViewModal } from "./components/modals/saveAsView/SaveAsViewModal";
import { TagsTreeModal } from "./components/modals/tagsTreeModal/TagsTreeModal";
import { useTrendingContext } from "./context/TrendingContext";
import { useTagsDataTable } from "./hooks/useTagsDataTable";
import { CursorData, ViewTag } from "./models/TrendingModels";
import {
	deleteAllViewTagsFromView,
	saveTagDefaults,
	upsertView,
	upsertViewTag,
} from "./repository/TrendingRepository";
import { useTagsTableStyles } from "./styles/TagsTableStyles";
import { formatTimeDiffWithMs } from "./utils/formatTimeDiffWithMs";
import { getRandomColor } from "./utils/tagColors";

interface TableComponentProps {
	cursorData: CursorData;
	chartData: ChartData;
	getTagsFromAPI: Function;
	viewTagForDelete: ViewTag | null;
	setViewTagForDelete: Dispatch<ViewTag | null>;
	selectedRowTagId: number | null;
	setSelectedRowTagId: Dispatch<number | null>;
	handleRightClick: (e: any, tagId: number | null) => void;
	openContextMenu: boolean;
	setOpenContextMenu: Dispatch<boolean>;
	setContextMenuPosition: Dispatch<{
		x: number;
		y: number;
	}>;
	contextMenuPosition: {
		x: number;
		y: number;
	};
}

const TableComponent: React.FC<TableComponentProps> = ({
	chartData,
	cursorData,
	getTagsFromAPI,
	viewTagForDelete,
	setViewTagForDelete,
	selectedRowTagId,
	setSelectedRowTagId,
	handleRightClick,
	setContextMenuPosition,
	contextMenuPosition,
	openContextMenu,
	setOpenContextMenu,
}) => {
	const classes = useTagsTableStyles();

	const {
		state: { viewTags, viewSelected },
		actions: { setViewTags, setViewTagsAndRefetch },
	} = useTrendingContext();
	const [localValues, setLocalValues] = useState<{ [key: number]: string }>({});

	const [error, setError] = useState("");
	const [isLoading, setIsLoading] = useState(false);

	const [tagsTreeModalOpen, setTagsTreeModalOpen] = useState(false);
	const [saveAsViewModalOpen, setSaveAsViewModalOpen] = useState(false);
	const [loadViewOpen, setLoadViewOpen] = useState(false);

	const [snackbarMessage, setSnackbarMessage] = useState("");

	const dataTable = useTagsDataTable({
		viewTagsMap: viewTags,
		chartData,
		cursorData,
	});

	const handleChange = (
		tagId: number,
		value: number,
		property: "MinScale" | "MaxScale",
	) => {
		const newTags = structuredClone(viewTags);

		newTags[tagId]["viewTag"][property] = value;
		newTags[tagId]["viewTag"]["IsAutoScale"] = false;
		setViewTags(newTags);
	};
	const handleChangeScale = (
		value: number,
		scale: "MinScale" | "MaxScale",
		tagId: number,
	) => {
		handleChange(tagId, value, scale);
	};
	const handleChangeAlias = (tagId: number, newName: string) => {
		const newTags = structuredClone(viewTags);
		newTags[tagId]["viewTag"]["Alias"] = newName;
		setViewTags(newTags);
	};
	const handleVisibilityChange = (tagId: any, value: any) => {
		const newTags = structuredClone(viewTags);
		newTags[tagId]["viewTag"]["IsVisible"] = value;
		setViewTags(newTags);
	};

	const handleUnitChange = (tagId: number, newUnit: string) => {
		const newTags = structuredClone(viewTags);
		newTags[tagId]["viewTag"]["Unit"] = newUnit;
		setViewTags(newTags);
	};

	const handleAutoScaleChange = (tagId: number, checked: boolean) => {
		const newTags = structuredClone(viewTags);
		newTags[tagId]["viewTag"]["IsAutoScale"] = !checked;
		setViewTags(newTags);
	};

	const handleColorChange = (tagId: any, value: any) => {
		const newTags = structuredClone(viewTags);
		newTags[tagId]["viewTag"]["Color"] = value;
		setViewTags(newTags);
	};

	const handleDeleteTag = (tagId: number) => {
		const newTags = structuredClone(viewTags);
		delete newTags[tagId];
		setViewTagsAndRefetch(newTags);
	};

	const handleAddTag = (newTag: any) => {
		const newTags = structuredClone(viewTags);
		newTags[newTag.TagId] = {
			viewTag: {
				...newTag,
				IsVisible: true,
				TagType: newTag.TagTypeCode,
				Color: newTag.Color || getRandomColor(),
				IsAutoScale: newTag.MinScale || newTag.MaxScale ? false : true,
			},
			order: Object.keys(viewTags).length,
		};
		setViewTagsAndRefetch(newTags);
	};

	const handleAbleScales = (tagId: number, checked: boolean) => {
		if (checked) {
			handleAutoScaleChange(tagId, checked);
		}
	};

	const handleClose = () => setTagsTreeModalOpen(false);

	const ContextMenu = ({ x, y, options }: any) => {
		const handleContextMenuClick = (e: any) => {
			e.stopPropagation(); // Prevent click inside the menu from closing it
		};

		return (
			<div
				className="context-menu"
				style={{ top: y, left: x }}
				onClick={handleContextMenuClick}
			>
				<ul>
					{options.map((option: any) => (
						<li key={option.id} onClick={option.onClick}>
							{option.label}
						</li>
					))}
				</ul>
			</div>
		);
	};

	const contextMenuOptions = [];

	if (viewTagForDelete) {
		contextMenuOptions.push({
			id: "delete",
			label: "Remove Tag",
			onClick: () => {
				handleDelete();
				setOpenContextMenu(false);
			},
		});
	}
	contextMenuOptions.push(
		...[
			{
				id: "add",
				label: "Tags Tree",
				onClick: () => {
					setTagsTreeModalOpen(true);
					setOpenContextMenu(false);
				},
			},
			{
				id: "saveDefault",
				label: "Save Default To Tag",
				onClick: () => {
					handleSaveDefaultToTag();
					setOpenContextMenu(false);
				},
			},
			{
				id: "saveView",
				label: "Save View",
				onClick: () => {
					handleSaveView();
					setOpenContextMenu(false);
				},
			},
			{
				id: "saveViewAs",
				label: "Save View as",
				onClick: () => {
					setSaveAsViewModalOpen(true);
					setOpenContextMenu(false);
				},
			},
			{
				id: "loadView",
				label: "Load View",
				onClick: () => {
					setLoadViewOpen(true);
					setOpenContextMenu(false);
				},
			},
		],
	);

	const handleTagSelect = (selectedTag: any) => {
		const alreadySelected = viewTags[selectedTag.TagId];
		if (!alreadySelected) {
			handleAddTag(selectedTag);
		} else {
			setError(`${selectedTag.TagName} is already selected`);
		}
	};

	const handleDelete = async () => {
		if (!isNil(viewTagForDelete)) {
			if (viewSelected?.ViewId) {
				handleDeleteTag(viewTagForDelete.TagId);
			}
			setSelectedRowTagId(null); // Reset the selected row index
			setViewTagForDelete(null);
		}
	};

	const handleSaveView = async () => {
		if (!viewSelected) {
			setSaveAsViewModalOpen(true);
			return;
		} else {
			setIsLoading(true);
			if (viewSelected?.ViewId) {
				const resp = await upsertView(
					viewSelected?.ViewId,
					viewSelected.ViewName,
					viewSelected.IsPublic,
				);
				if (resp.ok) {
					const deleteResp = await deleteAllViewTagsFromView(
						viewSelected.ViewId,
					);
					if (!deleteResp.ok) {
						setError(deleteResp.message);
					}
					const results = await Promise.allSettled(
						Object.values(viewTags)
							.sort((a, b) => b.order - a.order)
							.map(
								({
									viewTag: {
										TagId,
										Color,
										MinScale,
										MaxScale,
										IsAutoScale,
										IsVisible,
										Unit,
									},
								}) => {
									upsertViewTag(
										viewSelected?.ViewId,
										TagId,
										Color,
										MinScale,
										MaxScale,
										IsVisible,
										IsAutoScale,
										Unit,
									).then((r) => {
										if (!r.ok) {
											setError(r.message);
										}
									});
								},
							),
					);
					results.forEach((result, index) => {
						if (result.status === "rejected") {
							setError(`Tag ${index} failed with reason: ${result.reason}`);
						} else {
							setSelectedRowTagId(null);
							setSnackbarMessage("View saved successfully");
						}
					});
				} else {
					setError(resp.message);
				}
			}
			setIsLoading(false);
		}
	};

	const handleCloseSnackbar = (
		event?: React.SyntheticEvent,
		reason?: string,
	) => {
		if (reason === "clickaway") {
			return;
		}

		setSnackbarMessage("");
	};

	useEffect(() => {
		const handleGlobalClick = (e: any) => {
			if (selectedRowTagId !== null) {
				setSelectedRowTagId(null);
			}
		};

		const handleKeyDown = (e: any) => {
			if (e.key === "Escape" && selectedRowTagId !== null) {
				setSelectedRowTagId(null);
			}
		};

		// Attach event listeners
		document.addEventListener("click", handleGlobalClick);
		document.addEventListener("keydown", handleKeyDown);

		// Cleanup function
		return () => {
			document.removeEventListener("click", handleGlobalClick);
			document.removeEventListener("keydown", handleKeyDown);
		};
	}, [selectedRowTagId]);

	const handleSaveDefaultToTag = async () => {
		if (viewSelected && selectedRowTagId !== null) {
			const {
				TagId,
				Alias,
				Color,
				MinScale,
				MaxScale,
				Unit,
				IsVisible,
				IsAutoScale,
			} = viewTags[selectedRowTagId].viewTag;

			const saveTagDefaultsResp = await saveTagDefaults(
				TagId,
				Alias,
				Color,
				MinScale,
				MaxScale,
				Unit,
			);
			const upsertViewTagResp = await upsertViewTag(
				viewSelected?.ViewId,
				TagId,
				Color,
				MinScale,
				MaxScale,
				IsVisible,
				IsAutoScale,
				Unit,
			);

			if (!saveTagDefaultsResp.ok) {
				setError(saveTagDefaultsResp.message);
			}
			if (!upsertViewTagResp.ok) {
				setError(upsertViewTagResp.message);
			}
			setSelectedRowTagId(null);
		}
	};

	return (
		<>
			<Grid
				container
				spacing={1}
				alignContent={"flex-start"}
				style={{
					height: "100%",
					maxHeight: "100%",
					overflowY: "auto",
				}}
			>
				<Grid
					item
					xs={12}
					style={{
						position: "sticky",
						background: "white",
						top: 0,
						left: 0,
						zIndex: 1000,
					}}
				>
					<div style={{ overflowX: "auto", width: "100%" }}>
						<table
							style={{
								width: "100%",
								tableLayout: "fixed",
								textAlign: "center",
							}}
						>
							<thead>
								<tr>
									<th
										className={`${classes.topTitles} ${classes.viewSelected}`}
									>
										{viewSelected ? (
											<Button
												variant="text"
												style={{ color: "#0075ff" }}
												className={classes.viewSelected}
												onClick={() => setLoadViewOpen(true)}
												startIcon={
													viewSelected.IsPublic ? (
														<></>
													) : (
														<LockIcon fontSize="small" />
													)
												}
											>
												{viewSelected?.ViewName || ""}
											</Button>
										) : (
											<Button
												variant="text"
												style={{ color: "#0075ff" }}
												className={classes.viewSelected}
												onClick={() => setSaveAsViewModalOpen(true)}
											>
												<i>
													<SaveIcon />
													(Save view as)
												</i>
											</Button>
										)}
									</th>
									<th
										colSpan={4}
										className={`${classes.topTitles} ${classes.scale}`}
									>
										Scale
									</th>
									<th className={`${classes.topTitles} ${classes.xStats}`}>
										{cursorData
											? (() => {
													const dateX1 = new Date(cursorData.x1);
													const dateX2 = new Date(cursorData.x2);

													const formattedDate = dateX1.toLocaleString("en-US", {
														hour12: false,
														month: "short",
														day: "numeric",
														...(dateX1.getFullYear() !==
															dateX2.getFullYear() && { year: "numeric" }),
													});

													const dateText = `${formattedDate}`;

													return (
														<>
															<span
																style={{
																	height: "10px",
																	width: "10px",
																	backgroundColor: "red",
																	borderRadius: "50%",
																	display: "inline-block",
																	marginRight: "5px",
																}}
															></span>
															{dateText}
														</>
													);
												})()
											: null}
									</th>
									<th className={`${classes.topTitles} ${classes.xStats}`}>
										{cursorData
											? (() => {
													const dateX1 = new Date(cursorData.x1);
													const dateX2 = new Date(cursorData.x2);

													const formattedDate = dateX2.toLocaleString("en-US", {
														hour12: false,
														month: "short",
														day: "numeric",
														...(dateX1.getFullYear() !==
															dateX2.getFullYear() && { year: "numeric" }),
													});

													const dateText = `${formattedDate}`;

													return (
														<>
															<span
																style={{
																	height: "10px",
																	width: "10px",
																	backgroundColor: "blue",
																	borderRadius: "50%",
																	display: "inline-block",
																	marginRight: "5px",
																}}
															></span>
															{dateText}
														</>
													);
												})()
											: null}
									</th>
									<th
										colSpan={3}
										className={`${classes.topTitles} ${classes.xStats}`}
									>
										{cursorData
											? formatTimeDiffWithMs(
													cursorData.x1 as any,
													cursorData.x2 as any,
												)
											: null}
									</th>
								</tr>
							</thead>
						</table>
						<table
							style={{
								width: "100%",
								tableLayout: "fixed",
								textAlign: "center",
							}}
						>
							<thead>
								<tr className={classes.bottomTitlesRow}>
									<th className={classes.visible}>Visible</th>
									<th className={classes.aliasCol}>Alias / Tagname</th>
									<th className={classes.color}>Color</th>
									<th className={classes.inputCol}>Min Scale</th>
									<th className={classes.inputCol}>Max Scale</th>
									<th className={classes.autoScale}>AutoScale</th>
									<th className={classes.unit}>Unit</th>
									<th>
										{" "}
										{cursorData
											? (() => {
													const date = new Date(cursorData.x1);
													const formattedHour = date.toLocaleString("en-US", {
														hour12: false,
														hour: "2-digit",
														minute: "2-digit",
														second: "2-digit",
													});

													const milliseconds = (date.getMilliseconds() / 1000)
														.toFixed(2)
														.substring(2);
													const dateText = `${formattedHour}.${milliseconds} hs`;

													return <>{dateText}</>;
												})()
											: null}{" "}
									</th>
									<th>
										{" "}
										{cursorData
											? (() => {
													const date = new Date(cursorData.x2);
													const formattedHour = date.toLocaleString("en-US", {
														hour12: false,
														hour: "2-digit",
														minute: "2-digit",
														second: "2-digit",
													});

													const milliseconds = (date.getMilliseconds() / 1000)
														.toFixed(2)
														.substring(2);
													const dateText = `${formattedHour}.${milliseconds} hs`;

													return <>{dateText}</>;
												})()
											: null}{" "}
									</th>
									<th>Y2-Y1</th>
									<th>Min</th>
									<th>Max</th>
								</tr>
							</thead>
						</table>
					</div>
				</Grid>
				<Grid item xs={12}>
					<div style={{ width: "100%", height: "100%", maxHeight: "100%" }}>
						<table
							cellPadding="4px"
							style={{
								width: "100%",
								tableLayout: "fixed",
								textAlign: "center",
							}}
						>
							<tbody>
								{Object.values(viewTags).length ? (
									Object.values(viewTags).map(({ viewTag: tag }) => (
										<tr
											key={tag.TagName}
											// onClick={() => handleRowClick(index)}
											onContextMenu={(e) => handleRightClick(e, tag.TagId)}
											className={classes.rowHover}
										>
											<td className={`${classes.visible} ${classes.checkbox}`}>
												<input
													type="checkbox"
													checked={tag.IsVisible}
													onChange={(e) =>
														handleVisibilityChange(tag.TagId, e.target.checked)
													}
												/>
											</td>
											<td className={classes.aliasCol}>
												<Tooltip
													title={tag.TagName}
													placement="top"
													arrow
													interactive
													enterDelay={500}
												>
													<div
														style={{ display: "flex", alignItems: "center" }}
													>
														{tag.TagType && (
															<div
																className={classes.dataType}
																style={{
																	backgroundColor:
																		tag.TagType === "A" ? "#AD48C4" : "#C46F48",
																}}
															>
																{tag.TagType}
															</div>
														)}
														<input
															type="text"
															style={{ width: "100%" }}
															value={tag.Alias || tag.TagName}
															onFocus={(e) => e.target.select()}
															onChange={(e) => {
																const newAlias = e.target.value;
																handleChangeAlias(tag.TagId, newAlias);
															}}
														/>
													</div>
												</Tooltip>
											</td>
											<td className={classes.color}>
												<ColorPicker
													onChange={handleColorChange}
													color={tag.Color}
													elementId={tag.TagId}
												/>
											</td>
											<td className={classes.inputCol}>
												<input
													type="number"
													className={classes.numberInput}
													style={{
														width: "100%",
														...(tag.IsAutoScale
															? {
																	backgroundColor: "rgba(239, 239, 239, 0.3)",
																	color: "rgba(84, 84, 84, 1)",
																	border: "1px solid rgba(118, 118, 118, 0.3)",
																}
															: {}),
													}}
													value={tag.MinScale}
													onClick={() =>
														handleAbleScales(tag.TagId, tag.IsAutoScale)
													}
													onFocus={(e) => e.target.select()}
													onChange={(e) =>
														handleChangeScale(
															e.target.value as any,
															"MinScale",
															tag.TagId,
														)
													}
												/>
											</td>
											<td className={classes.inputCol}>
												<input
													type="number"
													className={classes.numberInput}
													style={{
														width: "100%",
														WebkitAppearance: "none",
														...(tag.IsAutoScale
															? {
																	backgroundColor: "rgba(239, 239, 239, 0.3)",
																	color: "rgba(84, 84, 84, 1)",
																	border: "1px solid rgba(118, 118, 118, 0.3)",
																}
															: {}),
													}}
													value={tag.MaxScale}
													onClick={() =>
														handleAbleScales(tag.TagId, tag.IsAutoScale)
													}
													onFocus={(e) => e.target.select()}
													onChange={(e) => {
														handleChangeScale(
															e.target.value as any,
															"MaxScale",
															tag.TagId,
														);
													}}
												/>
											</td>
											<td
												className={`${classes.autoScale} ${classes.checkbox}`}
											>
												<input
													type="checkbox"
													checked={!!tag.IsAutoScale}
													onChange={(e) =>
														handleAutoScaleChange(tag.TagId, tag.IsAutoScale)
													}
												/>
											</td>
											<td
												className={`${classes.autoScale} ${classes.checkbox}`}
											>
												<input
													type="text"
													style={{
														width: "100%",
													}}
													onClick={() =>
														handleAbleScales(tag.TagId, tag.IsAutoScale)
													}
													onChange={(e) => {
														const value = e.target.value;
														handleUnitChange(tag.TagId, value);
													}}
													value={tag.Unit === "-" ? "" : tag.Unit}
													onFocus={(e) => e.target.select()}
												/>
											</td>

											<td>{round(dataTable[tag.TagName]?.y1, 3) || 0}</td>

											<td className={classes.doubleInput}>
												{round(dataTable[tag.TagName]?.y2, 3) || 0}
											</td>
											<td>{round(dataTable[tag.TagName]?.y2y1, 3) || 0}</td>
											<td>
												{!isNaN(dataTable[tag.TagName]?.pmin.y * 0)
													? round(dataTable[tag.TagName]?.pmin.y, 3)
													: 0}{" "}
											</td>
											<td>
												{!isNaN(dataTable[tag.TagName]?.pmax.y * 0)
													? round(dataTable[tag.TagName]?.pmax.y)
													: 0}{" "}
											</td>
										</tr>
									))
								) : (
									<div style={{ padding: "1rem" }}>
										<div style={{ padding: "1rem" }}>
											No tag view detected.{" "}
										</div>
										<div style={{ padding: "1rem" }}>
											<Button
												onClick={() => setTagsTreeModalOpen(true)}
												variant="contained"
												color="primary"
											>
												{" "}
												Add tag
											</Button>
										</div>
										<div>
											<Button
												onClick={() => setLoadViewOpen(true)}
												variant="outlined"
												color="primary"
											>
												{" "}
												Load view
											</Button>
										</div>
									</div>
								)}
							</tbody>
						</table>
					</div>
				</Grid>
				{openContextMenu && (
					<ContextMenu
						x={contextMenuPosition.x}
						y={contextMenuPosition.y}
						options={contextMenuOptions}
					/>
				)}
				<TagsTreeModal
					open={tagsTreeModalOpen}
					handleClose={handleClose}
					onTagSelect={handleTagSelect}
				/>
				<SaveAsViewModal
					open={saveAsViewModalOpen}
					handleClose={(shouldUpdate: boolean) => {
						setSaveAsViewModalOpen(false);
						if (shouldUpdate) getTagsFromAPI(true).then(() => {});
					}}
				/>
				<LoadViewModal
					open={loadViewOpen}
					getTagsFromAPI={getTagsFromAPI}
					handleClose={(shouldUpdate: boolean) => {
						setLoadViewOpen(false);
					}}
				/>
			</Grid>
			<ErrorModal error={error} onHide={() => setError("")} />
			<Snackbar
				open={!!snackbarMessage}
				autoHideDuration={800}
				onClose={handleCloseSnackbar}
			>
				<Alert onClose={handleCloseSnackbar} severity="success">
					{snackbarMessage}
				</Alert>
			</Snackbar>
		</>
	);
};

export default TableComponent;
